import { Check, KeyRound, Minus, Plus } from 'lucide-react';
import {
  Badge,
  Button,
  Input,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Timeline,
  useToast,
} from '@/components/ui';
import { Body, Page, Panel } from '../common';

/*
  «Управление» — общий блок внизу меню каждого приложения. Его строит Kaname, а не автор
  приложения (docs/core-contract.md §5, §7, §9): настройки из паспорта, доступ (выдачи и что может
  роль), журнал изменений, состояние окружений. Здесь — только вид этих страниц.
*/

export function InfraManage({ view }: { view: string }) {
  if (view === 'manage-access') return <Access />;
  if (view === 'manage-journal') return <Journal />;
  if (view === 'manage-status') return <Status />;
  return <Settings />;
}

function Settings() {
  const { toast } = useToast();
  return (
    <Page>
      <PageHeader
        title="Настройки приложения"
        meta={<span>страницу строит Kaname из паспорта приложения</span>}
      />
      <Body>
        <Panel title="Уведомления" className="max-w-3xl">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Канал для тревог" defaultValue="#alerts-infra" />
            <Input label="Порог Load average" defaultValue="8.0" />
          </div>
        </Panel>
        <Panel title="Подключения" className="max-w-3xl">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <KeyRound className="size-4 text-fg-subtle" />
              <div className="flex flex-col">
                <span className="text-sm font-medium text-fg">Ключ центра мониторинга</span>
                <span className="text-xs text-fg-muted">задан 14.10 · после сохранения не показывается</span>
              </div>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => toast({ title: 'Откроется окно замены ключа' })}
            >
              Заменить
            </Button>
          </div>
        </Panel>
      </Body>
    </Page>
  );
}

const GRANTS = [
  { who: 'L1', kind: 'группа · связь со Slack', role: 'Наблюдатель' },
  { who: 'L2', kind: 'группа · связь со Slack', role: 'Дежурный' },
  { who: 'Анна', kind: 'человек · владелец', role: 'Администратор' },
];

const MATRIX: { action: string; roles: [boolean, boolean, boolean] }[] = [
  { action: 'Смотреть узлы и проблемы', roles: [true, true, true] },
  { action: 'Подтверждать и глушить', roles: [false, true, true] },
  { action: 'Переводить узел в обслуживание', roles: [false, true, true] },
  { action: 'Менять пороги и каналы', roles: [false, false, true] },
];

function Access() {
  return (
    <Page>
      <PageHeader
        title="Доступ"
        meta={<span>Kaname решает, кто и с какой ролью работает; приложение — что роль может</span>}
        actions={<Button leftIcon={<Plus />}>Выдать роль</Button>}
      />
      <Body>
        <Panel title="Выдачи">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Кому</TableHead>
                <TableHead>Кто это</TableHead>
                <TableHead>Роль</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {GRANTS.map((g) => (
                <TableRow key={g.who}>
                  <TableCell className="font-medium">{g.who}</TableCell>
                  <TableCell className="text-fg-muted">{g.kind}</TableCell>
                  <TableCell>
                    <Badge variant="neutral" solid>
                      {g.role}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Panel>
        <Panel title="Что может роль">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Действие</TableHead>
                <TableHead className="text-center">Наблюдатель</TableHead>
                <TableHead className="text-center">Дежурный</TableHead>
                <TableHead className="text-center">Администратор</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {MATRIX.map((m) => (
                <TableRow key={m.action}>
                  <TableCell>{m.action}</TableCell>
                  {m.roles.map((ok, i) => (
                    <TableCell key={i} className="text-center">
                      {ok ? (
                        <Check className="mx-auto size-4 text-success-fg" aria-label="можно" />
                      ) : (
                        <Minus className="mx-auto size-4 text-fg-subtle" aria-label="нельзя" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Panel>
      </Body>
    </Page>
  );
}

function Journal() {
  return (
    <Page>
      <PageHeader title="Журнал" meta={<span>записи не правятся и не удаляются</span>} />
      <Body>
        <Panel className="max-w-3xl">
          <Timeline
            items={[
              {
                id: 'j1',
                kind: 'change',
                time: '12:40',
                title: 'Порог Load average: 6.0 → 8.0',
                actor: 'Анна',
              },
              {
                id: 'j2',
                kind: 'change',
                time: '11:02',
                title: 'Выдана роль «Дежурный» группе L2',
                actor: 'Анна',
              },
              {
                id: 'j3',
                kind: 'ack',
                time: 'вчера 18:20',
                title: 'Заменён ключ центра мониторинга',
                actor: 'Михаил',
              },
              {
                id: 'j4',
                kind: 'change',
                time: '14.10',
                title: 'Приложение подключено к Kaname',
                actor: 'Анна',
              },
            ]}
          />
        </Panel>
      </Body>
    </Page>
  );
}

function Status() {
  return (
    <Page>
      <PageHeader title="Состояние" meta={<span>пульс раз в 30 секунд</span>} />
      <Body>
        <div className="grid gap-4 md:grid-cols-2">
          {[
            { env: 'Боевое', version: '1.14.2', up: '6 сут', ok: true },
            { env: 'Тестовое', version: '1.15.0-rc1', up: '3 ч', ok: true },
          ].map((e) => (
            <Panel
              key={e.env}
              title={e.env}
              aside={
                <Badge variant={e.ok ? 'success' : 'urgent'} solid>
                  {e.ok ? 'Работает' : 'Упало'}
                </Badge>
              }
            >
              <dl className="grid grid-cols-2 gap-y-2 text-sm">
                <dt className="text-fg-muted">Версия</dt>
                <dd className="text-right font-medium text-fg tabular-nums">{e.version}</dd>
                <dt className="text-fg-muted">С запуска</dt>
                <dd className="text-right font-medium text-fg tabular-nums">{e.up}</dd>
                <dt className="text-fg-muted">Доступность за 7 дней</dt>
                <dd className="text-right font-medium text-fg tabular-nums">99,98 %</dd>
              </dl>
            </Panel>
          ))}
        </div>
      </Body>
    </Page>
  );
}
